import { Link, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useRef, useState } from 'react';
import { ScrollView } from 'react-native';
import { useQuery } from 'urql';
import FadeInContent from '@/components/Animation/FadeInContent';
import PaginatorIndeterminate from '@/components/Custom/PaginatorIndeterminate';
import type { TableNextProps } from '@/components/Custom/TableNext.types';
import EventStatsTable, {
  type EventStatsTableRowData,
  GetEventStatsTable,
} from '@/components/Event/EventStats/EventStatsTable';
import ActionBar from '@/components/UI/ActionBar';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import usePaginatedList from '@/hooks/usePaginatedList';

const PAGE_LIMIT = 25;
export default function EventStats() {
  const [sortColumn, setSortColumn] = useState<
    TableNextProps<EventStatsTableRowData>['sortColumn'] | null
  >(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const paginated = usePaginatedList({
    limit: PAGE_LIMIT,
    scrollViewRef,
    indeterminate: true,
  });
  const { eventId } = useLocalSearchParams<{
    eventId: string;
  }>();
  const [{ data, fetching }, refetch] = useQuery({
    query: GetEventStatsTable,
    variables: {
      eventId,
      groupBy: 'personId',
      rankBy: sortColumn?.id,
      rankDirection: sortColumn?.direction,
      limit: paginated.limit,
      offset: paginated.offset,
    },
    requestPolicy: 'cache-and-network',
  });
  const effect = () => {
    refetch();
  };
  useFocusEffect(effect);
  return (
    <FadeInContent fetching={fetching}>
      <Box direction="vertical" flex space="md">
        <ActionBar>
          <Link
            href={{
              pathname: '/event/[eventId]/stats/share',
              params: {
                eventId,
              },
            }}
            asChild
          >
            <ActionButton
              forceLabel
              label="Share"
              icon="share-outline"
              variant="secondary"
            />
          </Link>
        </ActionBar>

        <ScrollView
          ref={scrollViewRef}
          contentContainerStyle={{
            flexGrow: 1,
          }}
        >
          <Box px="lg">
            <EventStatsTable
              data={data}
              fetching={fetching}
              sortColumn={sortColumn}
              setSortColumn={setSortColumn}
            />
          </Box>
          <PaginatorIndeterminate
            {...paginated}
            total={data?.badge_widget_values?.length}
          />
        </ScrollView>
      </Box>
    </FadeInContent>
  );
}
